import { reactive } from 'vue'
import { useSupabaseClientStore } from '../../stores/supaBaseClientStore'
import { useLogger } from '../../composables/useLogger'
import { getUserId } from '../../src/utils/userUtils'
import { processQueue } from '../utils/queue'
import { Image, AlbumImage, ImageBlob } from './index'
import type { ImageUpload } from './index'

const logger = useLogger()

export class Album {
    id: string;
    created_at: string | null;
    name: string | null;
    sort_order: number | null;
    albumImages: AlbumImage[];

    constructor(id: string) {
        this.id = id
        this.created_at = null
        this.name = null
        this.sort_order = null
        this.albumImages = reactive([])
    }

    // static method to get the supabase client
    static getSupabase() {
        const supabaseClientStore = useSupabaseClientStore()
        return supabaseClientStore.supabase
    }

    /**
     * Get an album from the database. 
     * @param {string} id - The id of the album to get.
     * @returns {Promise<Album>} The album.
     */
    static async fromId(id: string): Promise<Album> {
        const { data: albumData, error } = await Album.getSupabase()
            .from('albums')
            .select('*')
            .eq('id', id)
            .single()
        if (error) logger.throwSupabaseError(error, `Failed to get album with ID ${id}`);
        return await Album.fromDatabaseRow(albumData)
    }

    /**
     * Create an album from database data.
     * @param {object} row - The data to create the album from.
     * @returns {Promise<Album>} The album.
     */
    static async fromDatabaseRow(row: any): Promise<Album> {
        const album = new Album(row.id)
        album.created_at = row.created_at
        album.name = row.name
        album.sort_order = row.sort_order
        await album.populateAlbumImages()
        return album
    }

    /**
     * Get all albums from the database, sequentially, album by album.
     * @param {boolean} withBlob - Whether to populate the blob.
     * @returns {Promise<Album[]>} The albums.
     */
    static async allFromDatabaseOld(withBlob: boolean = true): Promise<Album[]> {
        const { data, error } = await Album.getSupabase()
            .from('albums')
            .select('*')
            .eq('user_id', getUserId())
            .order('sort_order', { ascending: true })
        if (error) {
            logger.throwSupabaseError(error, 'Failed to get all albums')
        }
        const albums: Album[] = []
        for (const album of data) {
            const albumobj = await Album.fromDatabaseRow(album)
            albums.push(albumobj)
        }
        return albums
    }

    /**
     * Get all albums with their images from the database in a single query.
     * @param {boolean} withBlob - Whether to populate the blob.
     * @returns {Promise<Album[]>} The albums.
     */
    static async allFromDatabase(withBlob: boolean = true): Promise<Album[]> {
        const userId = getUserId()
        const { data, error } = await Album.getSupabase()
            .from('albums')
            .select(`
                *,
                albumimages (
                    *,
                    images (*)
                )
            `)
            .eq('user_id', getUserId())
            .order('sort_order', { ascending: true })

        if (error) {
            logger.logErrorCaught('Error getting albums:', 'Album.ts', error)
            throw error
        }

        const albums: Album[] = []
        
        for (const albumData of data) {
            const album = new Album(albumData.id)
            album.created_at = albumData.created_at
            album.name = albumData.name
            album.sort_order = albumData.sort_order
            
            // Create AlbumImage instances from the joined data
            album.albumImages = reactive(
                albumData.albumimages
                    .map((albumImageData: any) => {
                        try {
                            // Create new Image instance and populate it
                            const image = new Image()
                            image.id = albumImageData.images.id
                            Object.assign(image, albumImageData.images)
                            
                            // Create new AlbumImage instance and populate it
                            const albumImage = new AlbumImage()
                            albumImage.id = albumImageData.id
                            Object.assign(albumImage, albumImageData)
                            albumImage.image = image
                            
                            return albumImage
                        } catch (e) {
                            logger.logErrorCaught('Error creating AlbumImage', 'Album.ts', e)
                            return null
                        }
                    })
                    .filter((ai: AlbumImage | null) => ai !== null)
                    .sort((a: AlbumImage, b: AlbumImage) => a.sort_order - b.sort_order)
            )
            
            albums.push(album)
        }

        return albums
    }


    async getImageBlobDict(awaitBlobLoad: boolean = false): Promise<Record<string, ImageBlob>> {
        const blobDict: Record<string, ImageBlob> = {}
        const promises = this.albumImages.map(async (albumImage) => {
            const imageBlob = await albumImage.image.getImageBlob()
            blobDict[albumImage.image.id] = imageBlob
        })

        if (awaitBlobLoad) {
            await Promise.all(promises)
        } else {
            Promise.all(promises).catch(console.error)
        }

        return blobDict
    }

    /**
     * Create a new album.
     * @param {string} name - The name of the album.
     * @returns {Promise<Album>} The new album.
     */
    static async createNewAlbum(name: string): Promise<Album> {
        const { data, error } = await Album.getSupabase()
            .from('albums')
            .insert({
                name: name,
                user_id:getUserId()
            })
            .select()
            .single()
        if (error) logger.throwSupabaseError(error, `Failed to create new album with name ${name}`);
        const newAlbum = await Album.fromDatabaseRow(data)
        return newAlbum
    }

    /**
     * Populate the album images.
     * @returns {Promise<void>}
     */
    async populateAlbumImages(): Promise<void> {
        const { data: albumImagesData, error } = await Album.getSupabase()
            .from('albumimages')
            .select('*')
            .eq('album_id', this.id)
        if (error) logger.throwSupabaseError(error, `Failed to get album images for album ${this.id}`);

        const albumImagesGetter = async (albumImageRow: any) => {
            const albumImage = await AlbumImage.fromDatabaseRow(albumImageRow)
            this.albumImages.push(albumImage)
        }
        await processQueue(albumImagesData, albumImagesGetter)
        this.albumImages.sort((a, b) => a.sort_order - b.sort_order)
    }

    /**
     * Add images to the album from uploads.
     * @param {object[]} imageUploads - The images to add to the album.
     * @returns {Promise<void>}
     */
    async addImagesFromUpload(imageUploads: ImageUpload[], userId: string, customSortOrders?: number[]): Promise<void> {
        logger.debug('Adding images to album:', { albumId: this.id })
        logger.debug('Images:', { number: imageUploads.length })

        const maxSortOrder = this.maxSortOrder || 0
        logger.debug('Max sort order:', { maxSortOrder: maxSortOrder })
        const newSortOrder = maxSortOrder + 1
        logger.debug('New sort order:', { newSortOrder: newSortOrder })

        if (customSortOrders && customSortOrders.length === imageUploads.length) {
            // Use custom sort orders if provided
            imageUploads.forEach((image, index) => {
                image.sort_order = customSortOrders[index]
            })
        } else {
            // Default behavior: sort by name and assign incremental sort orders
            imageUploads.sort((a, b) => a.name.localeCompare(b.name))
            imageUploads.forEach((image, index) => {
                image.sort_order = newSortOrder + index
            })
        }

        const imageUploader = async (imageUpload: any) => {
            const newImage = await Image.createFromUpload(imageUpload)

            const { data: newAlbumImageData, error } = await Album.getSupabase()
                .from('albumimages')
                .insert({
                    album_id: this.id,
                    image_id: newImage.id,
                    sort_order: imageUpload.sort_order,
                    user_id: userId
                })
                .select()
                .single()
            if (error) {
                logger.throwSupabaseError(error, `Failed to add image ${newImage.id} to album ${this.id}`)
            }
            const newAlbumImage = await AlbumImage.fromDatabaseRow(newAlbumImageData)
            newAlbumImage.image = newImage
            logger.debug('500: New album image created, pushing to album:', { newAlbumImage: newAlbumImage })
            logger.debug('501: Album before push:', { album: this })
            // do not push here - it is handled in the realtime listener
            // this.albumImages.push(newAlbumImage)
            logger.debug('502: Album after push:', { album: this })
        }
        await processQueue(imageUploads, imageUploader)
        logger.debug('Uploaded images with sort orders:', imageUploads)

        // sort the album images by sort_order
        this.sortAlbumImages()
    }

    sortAlbumImages(): void {
        this.albumImages = [...this.albumImages].sort((a, b) => a.sort_order - b.sort_order)
    }

    /**
     * Get the maximum sort_order of the album's images.
     * @returns {number|null} The maximum sort_order, or null if there are no images.
     */
    get maxSortOrder(): number | null {
        if (this.albumImages.length === 0) {
            return null;
        }
        return Math.max(...this.albumImages.map(albumImage => albumImage.sort_order));
    }

    /**
     * Get the first album image.
     * @returns {AlbumImage|null} The first album image, or null if there are no images.
     */
    getFirstAlbumImage(): AlbumImage | null {
        return this.albumImages.length > 0 ? this.albumImages[0] : null
    }

    /**
     * Delete an album from the database.
     * @param {string} albumId - The id of the album to delete.
     * @returns {Promise<void>}
     */
    static async deleteAlbum(albumId: string): Promise<void> {
        logger.debug('Deleting album:', { albumId: albumId })

        // Get all the image ids for the album
        const { data: albumImages, error: albumImagesError } = await Album.getSupabase()
            .from('albumimages')
            .select('image_id')
            .eq('album_id', albumId)
        if (albumImagesError) {
            logger.throwSupabaseError(albumImagesError, `Failed to get images for album ${albumId}`)
        }
        logger.debug('Album images to delete:', { albumImages: albumImages })
        const idsToDelete = albumImages.map(ai => ai.image_id)
        logger.debug('Album images ids to delete:', { idsToDelete: idsToDelete })

        // delete the entries in the albumimages table
        const { error: albumImagesError2 } = await Album.getSupabase()
            .from('albumimages')
            .delete()
            .eq('album_id', albumId)
        if (albumImagesError2) {
            logger.throwSupabaseError(albumImagesError2, `Failed to delete images for album ${albumId}`)
        }

        // delete the images with their blobs
        const imageDeleter = async (imageId: string) => {
            await Image.deleteImage(imageId)
        }
        await processQueue(idsToDelete, imageDeleter)

        // finally delete the album itself
        const { error: albumError } = await Album.getSupabase()
            .from('albums')
            .delete()
            .eq('id', albumId)
        if (albumError) {
            logger.throwSupabaseError(albumError, `Failed to delete album ${albumId}`)
        }
    }

    /**
     * Delete all albums from the database.
     * @returns {Promise<void>}
     */
    static async deleteAllAlbums(): Promise<void> {
        const { data, error } = await Album.getSupabase()
            .from('albums')
            .select('*')
            .eq('user_id', getUserId())
        if (error) logger.throwSupabaseError(error, 'Failed to get all albums for deletion');

        for (const album of data) {
            await Album.deleteAlbum(album.id)
        }
    }

    /**
     * Delete an AlbumImage from the album.
     * @param {string} albumImageId - The id of the AlbumImage to delete.
     * @returns {Promise<void>}
     */
    async deleteAlbumImage(albumImageId: string): Promise<void> {
        logger.debug('Deleting AlbumImage:', { albumImageId: albumImageId })

        // Find the AlbumImage in the current album
        const albumImageIndex = this.albumImages.findIndex((ai: AlbumImage) => ai.id === albumImageId)
        if (albumImageIndex === -1) {
            throw new Error('AlbumImage not found in this album')
        }
        const albumImage = this.albumImages[albumImageIndex]

        try {
            // Delete from Supabase database
            const { error: dbError } = await Album.getSupabase()
                .from('albumimages')
                .delete()
                .eq('id', albumImageId)
            if (dbError) {
                logger.throwSupabaseError(dbError, `Failed to delete album image ${albumImageId}`)
            }

            // Delete the image with blob and so on
            await Image.deleteImage(albumImage.image.id)

            // Remove from the album's albumImages array - happens automatically in the realtime listener
            // this.albumImages.splice(albumImageIndex, 1)

            logger.debug('AlbumImage deleted successfully:', { albumImageId: albumImageId })
        } catch (error) {
            logger.throwSupabaseError(error, `Failed to delete album image ${albumImageId}`)
        }
    }

    async updateName(newName: string): Promise<void> {
        const { error } = await Album.getSupabase()
            .from('albums')
            .update({ name: newName })
            .eq('id', this.id)
        if (error) {
            logger.throwSupabaseError(error, `Failed to update name for album ${this.id}`)
        }
    }

    async updateSortOrder(newSortOrder: number): Promise<void> {
        const { error } = await Album.getSupabase()
            .from('albums')
            .update({ sort_order: newSortOrder })
            .eq('id', this.id)
        if (error) {
            logger.throwSupabaseError(error, `Failed to update sort order for album ${this.id}`)
        }
        this.sort_order = newSortOrder; // Update the local property
    }

    static async batchUpdateAlbumOrder(updatedAlbums: { id: string, sort_order: number }[]) {
        const userId = getUserId()
        const updatedAlbumsWithUserId = updatedAlbums.map(album => ({
            ...album,
            user_id:userId
        }))
        const { error } = await Album.getSupabase()
            .from('albums')
            .upsert(
                updatedAlbumsWithUserId,
                { 
                    onConflict: 'id'
                }
            )

        if (error) {
            logger.throwSupabaseError(error, 'Failed to batch update album order')
        }
    }

    async replaceAlbumImageByRotated(albumImageId: string, direction: 'right' | 'left'): Promise<void> {
        // Find the image in the album
        const albumImage = this.albumImages.find(aImg => aImg.id === albumImageId)
        if (!albumImage) {
            throw new Error('Album image not found')
        }

        // Retrieve info on the old image
        const oldImage = albumImage.image
        const oldImageBlob = await oldImage.getImageBlob()

        // Create a new rotated blob
        const newBlob = await ImageBlob.rotateBlob(await oldImageBlob.getBlob(), direction)

        // Create a new Image from the rotated blob
        const newUploadedImage: ImageUpload = {
            blob: newBlob,
            name: oldImage.name,
            size: oldImage.size,
            type: oldImage.type
        }
        const newImage = await Image.createFromUpload(newUploadedImage)

        // Update the AlbumImage with the new Image ID
        const { error: updateError } = await Album.getSupabase()
            .from('albumimages')
            .update({ image_id: newImage.id })
            .eq('id', albumImageId)

        if (updateError) {
            logger.error('Error updating AlbumImage:', updateError)
            logger.throwSupabaseError(updateError, `Failed to update rotated image for album image ${albumImageId}`)
        }

        // Update the local AlbumImage object
        // commented out, since it is handled in the realtime listener
        // albumImage.image = newImage

        // Delete the old image
        await Image.deleteImage(oldImage.id)
    }
}